import i18next from 'i18next';
import type { Resource } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';

import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/de-at';
import 'dayjs/locale/fr';
import 'dayjs/locale/hu';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import 'dayjs/locale/ro';
import 'dayjs/locale/sk';

import cs from './locales/cs.json';
import deAt from './locales/de-at.json';
import de from './locales/de.json';
import en from './locales/en.json';
import fr from './locales/fr.json';
import hu from './locales/hu.json';
import it from './locales/it.json';
import nl from './locales/nl.json';
import roMd from './locales/ro-MD.json';
import ro from './locales/ro.json';
import sk from './locales/sk.json';

const DEFAULT_CULTURE = 'en-US';

const FALLBACK_LANGUAGE = 'en';

const DAYJS_LOCALES = new Set(['cs', 'de', 'de-at', 'en', 'fr', 'hu', 'it', 'nl', 'ro', 'sk']);

const resources: Resource = {
  cs: { translation: cs },
  de: { translation: de },
  'de-AT': { translation: deAt },
  en: { translation: en },
  fr: { translation: fr },
  hu: { translation: hu },
  it: { translation: it },
  nl: { translation: nl },
  ro: { translation: ro },
  'ro-MD': { translation: roMd },
  sk: { translation: sk },
};

function toDayjsLocale(culture: string): string {
  const normalized = culture.toLowerCase();
  if (DAYJS_LOCALES.has(normalized)) {
    return normalized;
  }
  const [language = normalized] = normalized.split('-');
  return DAYJS_LOCALES.has(language) ? language : FALLBACK_LANGUAGE;
}

export type CreateI18nOptions = {
  detectCulture?: () => string | null;
  persistCulture?: (culture: string) => void;
  onCultureApplied?: (culture: string) => void;
};

export type PortalI18n = {
  initI18n: () => Promise<void>;
  changeLanguage: (culture: string) => Promise<void>;
};

export function createI18n(options: CreateI18nOptions = {}): PortalI18n {
  const applyCultureSideEffects = (culture: string): void => {
    dayjs.locale(toDayjsLocale(culture));
    options.onCultureApplied?.(culture);
  };

  return {
    async initI18n(): Promise<void> {
      const culture = options.detectCulture?.() ?? DEFAULT_CULTURE;
      await i18next
        .use(ICU)
        .use(initReactI18next)
        .init({
          resources,
          lng: culture,
          fallbackLng: FALLBACK_LANGUAGE,
          keySeparator: false,
          nsSeparator: false,
          interpolation: {
            escapeValue: false,
          },
        });
      applyCultureSideEffects(culture);
    },
    async changeLanguage(culture: string): Promise<void> {
      options.persistCulture?.(culture);
      await i18next.changeLanguage(culture);
      applyCultureSideEffects(culture);
    },
  };
}
